import SendIcon from '@mui/icons-material/Send';
import {LoadingButton} from '@mui/lab';
import {Box, TextField} from '@mui/material';
import {useFormik} from 'formik';
import {useSnackbar} from 'notistack';
import {useState} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {
  AssetHumanGroupMessage,
  AssetHumanGroupMessageInputBody,
} from '../../interfaces/AssetHumanGroupMessage';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import SnackbarMessages from '../common/SnackbarMessages';
import {AssetHumanGroupSelect} from './AssetHumanGroupSelect';

interface Props {
  onSubmitted?: () => void;
}

const AssetHumanGroupMessageCreate = ({onSubmitted}: Props) => {
  /**********/
  /* submit */
  /**********/
  const {enqueueSnackbar} = useSnackbar();
  const [submittedInProgress, setSubmittedInProgress] = useState(false);

  const submitData = async (data: AssetHumanGroupMessageInputBody) => {
    setSubmittedInProgress(true);

    try {
      const endpoint = `${apiBaseUrl}/group-message`;
      const resp = await API.post<AssetHumanGroupMessage>(endpoint, {
        ...data,
        id: data.id ?? undefined,
      });
      enqueueSnackbar(resp.data.message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      formik.setFieldValue('message', null);
      onSubmitted?.();
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_f6da6dc4");
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setSubmittedInProgress(false);
  };

  /*********/
  /* input */
  /*********/
  const getFormikValues = () => ({
    id: null,
    message: null,
  });

  const formik = useFormik<AssetHumanGroupMessageInputBody>({
    initialValues: getFormikValues(),
    onSubmit: (values) => submitData(values),
  });

  return (
    <Box
      component="form"
      position="relative"
      display="flex"
      gap={1}
      onSubmit={formik.handleSubmit}
    >
      <AssetHumanGroupSelect
        value={formik.values.id}
        label="Send to"
        nullLabel="Non-Emergency Broadcast"
        name="id"
        disableClearable
        sx={{width: 200}}
        onChange={(v) => formik.setFieldValue('id', v)}
      />

      <TextField
        value={formik.values.message ?? ''}
        name="message"
        fullWidth
        placeholder="Type Message..."
        error={!!formik.touched.message && !!formik.errors.message}
        helperText={formik.touched.message && formik.errors.message}
        onChange={formik.handleChange}
        inputProps={{maxLength: 40}}
        InputProps={{
          endAdornment: (
            <LoadingButton
              variant="contained"
              disabled={!formik.values.message}
              loading={submittedInProgress}
              type="submit"
            >
              <SendIcon />
            </LoadingButton>
          ),
        }}
      />
    </Box>
  );
};

export default AssetHumanGroupMessageCreate;
